import { useEffect } from "react";
import { useFileStore } from "../../../../store/useFileStore";

const DocLeafletMapsMDX = () => {
    // eslint-disable-next-line no-unused-vars
    const [_, setStore] = useFileStore((store) => store);

    useEffect(() => {
        let value = `---
sidebar_position: 2
sidebar_label: Leaflet Map
---

# Leaflet Map

Leaflet is a lightweight and easy-to-use JavaScript library that allows you to create mobile-friendly and interactive maps. Leaflet supports many types of map layers, such as tile, vector, image, and geoJSON. Leaflet also provides many plugins and extensions that add more features and functionalities to your maps, such as clustering, geocoding, routing, and drawing.

You can find the Leaflet map components in the \`src/views/LeafletMaps.jsx\` file.

import CodePreview from "@site/src/components/CodePreview/CodePreview";
import Leaflet from "@site/src/components/Maps/Leaflet/Leaflet";
import Tabs from '@theme/Tabs';
import TabItem from '@theme/TabItem';

## Examples

### Voyager
Voyager Map provides an interactive and customizable mapping solution for displaying geographic data with ease and flexibility. Leaflet Map [Preview](https://demo.reactadmin.com/bootstrap/basix-admin/components/maps/leaflet-maps)

<Tabs>
    <TabItem value="preview" label="Preview">
        <CodePreview>
            <Leaflet 
                attr='&copy; <a href="http://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/attributions">CARTO</a>' 
                url="https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png"
            />
        </CodePreview>
    </TabItem>
    
    <TabItem value="code" label="Code">
        \`\`\`jsx title="Voyager"
        import { MapContainer, Marker, Popup, TileLayer } from "react-leaflet";
        import Card from "@/components/Card/Card";
        import { CardBody } from "react-bootstrap";

        const MyComponent = () => {
            const position = [51.505, -0.09];

            return (
                <Card title="Voyager">
                    <CardBody>
                        <MapContainer
                            style={{ width: "100%", height: "400px" }}
                            center={position}
                            zoom={16}
                            scrollWheelZoom={false}
                        >
                            <TileLayer
                                attribution='&copy; <a href="http://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/attributions">CARTO</a>'
                                url="https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png"
                            />
                            <Marker position={position} >
                                <Popup>
                                    A pretty CSS3 popup. <br /> Easily customizable.
                                </Popup>
                            </Marker>
                        </MapContainer>
                    </CardBody>
                </Card>
            );
        };

        export default MyComponent;
        \`\`\`
    </TabItem>
</Tabs>

<br/>
### Street Map
Street Map provides a convenient and customizable solution for incorporating street-level maps into React applications, facilitating location-based functionalities. Leaflet Map [Preview](https://demo.reactadmin.com/bootstrap/basix-admin/components/maps/leaflet-maps)

<Tabs>
    <TabItem value="preview" label="Preview">
        <CodePreview>
            <Leaflet
                attr='&copy; <a href="http://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/attributions">CARTO</a>'
                url='https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png'
            />
        </CodePreview>
    </TabItem>
    
    <TabItem value="code" label="Code">
        \`\`\`jsx title="Street Map"
        import { MapContainer, Marker, Popup, TileLayer } from "react-leaflet";
        import Card from "@/components/Card/Card";
        import { CardBody } from "react-bootstrap";

        const MyComponent = () => {
            const position = [51.505, -0.09];

            return (
                <Card title="Street Map">
                    <CardBody>
                        <MapContainer
                            style={{ width: "100%", height: "400px" }}
                            center={position}
                            zoom={16}
                            scrollWheelZoom={false}
                        >
                            <TileLayer
                                attribution='&copy; <a href="http://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/attributions">CARTO</a>'
                                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                            />
                            <Marker position={position} >
                                <Popup>
                                    A pretty CSS3 popup. <br /> Easily customizable.
                                </Popup>
                            </Marker>
                        </MapContainer>
                    </CardBody>
                </Card>
            );
        };

        export default MyComponent;
        \`\`\`
    </TabItem>
</Tabs>

<br/>
### World Imagery
World Imagery Map offers a comprehensive and visually stunning solution for integrating high-resolution satellite imagery maps into React applications for global mapping needs. Leaflet Map [Preview](https://demo.reactadmin.com/bootstrap/basix-admin/components/maps/leaflet-maps)

<Tabs>
    <TabItem value="preview" label="Preview">
        <CodePreview>
            <Leaflet
                attr='&copy; <a href="http://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/attributions">CARTO</a>'
                url='https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}'
            />
        </CodePreview>
    </TabItem>
    
    <TabItem value="code" label="Code">
        \`\`\`jsx title="World Imagery"
        import { MapContainer, Marker, Popup, TileLayer } from "react-leaflet";
        import Card from "@/components/Card/Card";
        import { CardBody } from "react-bootstrap";

        const MyComponent = () => {
            const position = [51.505, -0.09];

            return (
                <Card title="World Imagery">
                    <CardBody>
                        <MapContainer
                            style={{ width: "100%", height: "400px" }}
                            center={position}
                            zoom={16}
                            scrollWheelZoom={false}
                        >
                            <TileLayer
                                attribution='&copy; <a href="http://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/attributions">CARTO</a>'
                                url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
                            />
                            <Marker position={position} >
                                <Popup>
                                    A pretty CSS3 popup. <br /> Easily customizable.
                                </Popup>
                            </Marker>
                        </MapContainer>
                    </CardBody>
                </Card>
            );
        };

        export default MyComponent;
        \`\`\`
    </TabItem>
</Tabs>

<br/>
### Dark Matter
Dark Matter Map provides a sleek and modern solution for integrating dark-themed maps into React applications, ideal for various visualization needs. Leaflet Map [Preview](https://demo.reactadmin.com/bootstrap/basix-admin/components/maps/leaflet-maps)

<Tabs>
    <TabItem value="preview" label="Preview">
        <CodePreview>
            <Leaflet 
                attr='&copy; <a href="http://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/attributions">CARTO</a>'
                url='https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png'
            />
        </CodePreview>
    </TabItem>
    
    <TabItem value="code" label="Code">
        \`\`\`jsx title="Dark Matter"
        import { MapContainer, Marker, Popup, TileLayer } from "react-leaflet";
        import Card from "@/components/Card/Card";
        import { CardBody } from "react-bootstrap";

        const MyComponent = () => {
            const position = [51.505, -0.09];

            return (
                <Card title="Dark Matter">
                    <CardBody>
                        <MapContainer
                            style={{ width: "100%", height: "400px" }}
                            center={position}
                            zoom={16}
                            scrollWheelZoom={false}
                        >
                            <TileLayer
                                attribution='&copy; <a href="http://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/attributions">CARTO</a>'
                                url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
                            />
                            <Marker position={position} >
                                <Popup>
                                    A pretty CSS3 popup. <br /> Easily customizable.
                                </Popup>
                            </Marker>
                        </MapContainer>
                    </CardBody>
                </Card>
            );
        };

        export default MyComponent;
        \`\`\`
    </TabItem>
</Tabs>

You have need more example please visit <a href="https://react-leaflet.js.org/docs/start-introduction/" target="_blank">React Leaflet</a>

<style>
    {\`
        .leaflet-top, .leaflet-bottom{
            z-index: 998;
        }
    \`}
</style>
`;

        setStore({ docLeafletMapsMDX: value });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return null;
};

export default DocLeafletMapsMDX;
